import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { MapPin, Phone, Mail, Globe, ArrowRight, Clock, UtensilsCrossed, Menu, X } from "lucide-react";
import LocationMap from "@/components/LocationMap";

const DAY_LABELS = { mon: "Lundi", tue: "Mardi", wed: "Mercredi", thu: "Jeudi", fri: "Vendredi", sat: "Samedi", sun: "Dimanche" };
const ALL_SECTIONS = ["hero", "about", "services", "why_us", "video", "gallery", "hours", "menu", "custom_text", "location", "contact"];

// Theme colour tokens — light = white/slate, dark = deep-slate/near-black. The
// employer picks in the Studio; everything downstream (nav, sections, footer)
// reads from these tokens instead of hardcoded colours so switching is
// instantaneous and coherent.
const THEMES = {
  light: {
    bg: "#FFFFFF", bgAlt: "#F8FAFC", card: "#FFFFFF", cardBorder: "#E2E8F0",
    text: "#0F172A", textMuted: "#64748B", navBg: "rgba(255,255,255,0.85)",
    heroBgGrad: "linear-gradient(135deg,#F8FAFC 0%,#FFFFFF 60%,#F8FAFC 100%)",
    whyBg: "#0F172A", whyText: "#FFFFFF", whyMuted: "rgba(255,255,255,0.9)",
    borderDashed: "#CBD5E1",
  },
  dark: {
    bg: "#0B1220", bgAlt: "#111C2E", card: "#152238", cardBorder: "#1F2E48",
    text: "#F1F5F9", textMuted: "#94A3B8", navBg: "rgba(11,18,32,0.9)",
    heroBgGrad: "linear-gradient(135deg,#0B1220 0%,#111C2E 60%,#0B1220 100%)",
    whyBg: "#050912", whyText: "#F1F5F9", whyMuted: "rgba(241,245,249,0.85)",
    borderDashed: "#334155",
  },
};

export default function SitePublic() {
  const { slug, pageSlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    http.get(`/site-data/${slug}`)
      .then((r) => { setData(r.data); http.post("/site-events", { slug, event: "view" }).catch(() => {}); })
      .catch(() => setNotFound(true));
  }, [slug]);

  const c = data?.site?.content || {};
  const co = data?.company || {};
  const accent = c.accent_color || "#F97316";
  const theme = THEMES[c.theme === "dark" ? "dark" : "light"];

  const pages = useMemo(() => {
    if (Array.isArray(c.pages) && c.pages.length) return c.pages;
    return [{ slug: "home", title: "Accueil", sections: ALL_SECTIONS }];
  }, [c.pages]);
  const currentPage = useMemo(() => pageSlug ? pages.find((p) => p.slug === pageSlug) || null : pages[0], [pageSlug, pages]);

  useEffect(() => {
    if (!data) return;
    document.title = currentPage?.title ? `${co.name} — ${currentPage.title}` : `${co.name} — ${c.tagline || ""}`;
  }, [data, currentPage, co.name, c.tagline]);

  const track = (event) => http.post("/site-events", { slug, event }).catch(() => {});
  const pageUrl = (p) => (p.slug === pages[0].slug ? `/site/${slug}` : `/site/${slug}/${p.slug}`);
  const goPage = (p) => { nav(pageUrl(p)); setMobileOpen(false); };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-6xl font-bold text-[#1E3A8A]">404</p>
          <p className="mt-4 text-slate-600">Ce site n'existe pas ou n'a pas encore été publié.</p>
          <a href="/" className="mt-6 inline-block text-[#F97316] underline">Retour à Proxijob</a>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg, color: theme.textMuted }}><div className="animate-pulse">Chargement…</div></div>;
  if (!currentPage) return <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: theme.bg, color: theme.text }}><div><p className="text-6xl font-bold" style={{ color: accent }}>404</p><p className="mt-4">Page introuvable.</p><Link to={`/site/${slug}`} className="mt-6 inline-block underline" style={{ color: accent }}>Retour à l'accueil</Link></div></div>;

  const has = (name) => (currentPage.sections || ALL_SECTIONS).includes(name);
  const showMultiNav = pages.length > 1;

  return (
    <div className="min-h-screen" style={{ background: theme.bg, color: theme.text }} data-testid="site-public" data-theme={c.theme || "light"}>
      <header className="sticky top-0 z-40 backdrop-blur border-b" style={{ background: theme.navBg, borderColor: theme.cardBorder }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to={`/site/${slug}`} className="flex items-center gap-3 min-w-0">
            {c.logo_url && <img src={c.logo_url} alt={co.name} className="h-10 w-10 rounded-full object-cover shrink-0 border" style={{ borderColor: theme.cardBorder }} />}
            <span className="font-display font-bold text-xl truncate">{co.name}</span>
          </Link>
          {showMultiNav && (
            <nav data-testid="site-nav" className="hidden md:flex items-center gap-1">
              {pages.map((p) => {
                const active = currentPage.slug === p.slug;
                return (
                  <button
                    key={p.slug}
                    data-testid={`nav-${p.slug}`}
                    onClick={() => goPage(p)}
                    className="text-sm px-3 py-1.5 rounded-full transition-colors"
                    style={active ? { background: theme.text, color: theme.bg } : { color: theme.text }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = theme.bgAlt; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  >{p.title}</button>
                );
              })}
            </nav>
          )}
          <div className="flex items-center gap-2">
            <a href="#contact" onClick={() => track("contact_click")} className="hidden sm:inline-flex text-sm font-medium px-4 py-2 rounded-full text-white" style={{ backgroundColor: accent }}>{c.cta_text || "Nous contacter"}</a>
            {showMultiNav && (
              <button data-testid="site-mobile-menu" onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 rounded-full" style={{ color: theme.text }}>
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>
        {showMultiNav && mobileOpen && (
          <div className="md:hidden border-t" style={{ borderColor: theme.cardBorder, background: theme.bg }}>
            <div className="max-w-5xl mx-auto px-6 py-2 flex flex-col">
              {pages.map((p) => {
                const active = currentPage.slug === p.slug;
                return <button key={p.slug} onClick={() => goPage(p)} className="text-left py-2.5 px-2 rounded-lg" style={{ color: active ? accent : theme.text, fontWeight: active ? 600 : 400 }}>{p.title}</button>;
              })}
            </div>
          </div>
        )}
      </header>

      {has("hero") && (
        <section className="relative overflow-hidden py-20 md:py-28 px-6">
          <div className="absolute inset-0 -z-10" style={{ background: theme.heroBgGrad }} />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: accent }} />
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>{currentPage.hero_kicker || currentPage.title || c.tagline}</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight" style={{ color: theme.text }}>{currentPage.hero_title || c.hero_title}</h1>
            <p className="mt-6 text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: theme.textMuted }}>{currentPage.hero_sub || c.hero_sub}</p>
            <a href="#contact" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium shadow-lg" style={{ backgroundColor: accent }}>{c.cta_text || "Nous contacter"} <ArrowRight className="w-4 h-4" /></a>
          </div>
        </section>
      )}

      {has("about") && (
        <section className="py-16 md:py-24 px-6" style={{ background: theme.bgAlt }}>
          <div className="max-w-4xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: theme.textMuted }}>À propos</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tight" style={{ color: theme.text }}>{c.about_title}</h2>
            <p className="mt-6 leading-relaxed text-lg max-w-3xl whitespace-pre-line" style={{ color: theme.textMuted }}>{c.about_body}</p>
          </div>
        </section>
      )}

      {has("services") && c.services?.length > 0 && (
        <section className="py-16 md:py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>Nos services</p>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {c.services.map((s, i) => (
                <div key={i} className="rounded-2xl border p-6 transition-all hover:shadow-md" style={{ background: theme.card, borderColor: theme.cardBorder }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: accent }}>{i + 1}</div>
                  <p className="font-display font-semibold mt-4 text-lg" style={{ color: theme.text }}>{s.title}</p>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: theme.textMuted }}>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {has("why_us") && c.why_us?.length > 0 && (
        <section className="py-16 md:py-24 px-6" style={{ background: theme.whyBg, color: theme.whyText }}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>Pourquoi nous choisir</p>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {c.why_us.map((r, i) => (
                <div key={i} className="p-6">
                  <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center text-white text-lg font-bold mb-3" style={{ backgroundColor: accent }}>✓</div>
                  <p className="leading-snug" style={{ color: theme.whyMuted }}>{r}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {has("video") && c.video_url && (
        <section className="py-16 md:py-20 px-6" data-testid="site-video">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-center mb-6" style={{ color: accent }}>Vidéo</p>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-lg bg-black">
              {c.video_url.startsWith("/api/videos/")
                ? <video src={c.video_url} controls playsInline className="w-full h-full object-contain" />
                : <iframe src={c.video_url} title="Vidéo" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
              }
            </div>
          </div>
        </section>
      )}

      {has("custom_text") && (currentPage.custom_text || "").trim() && (
        <section className="py-16 md:py-20 px-6" data-testid="site-custom-text">
          <div className="max-w-3xl mx-auto">
            {currentPage.custom_text_title && <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ color: theme.text }}>{currentPage.custom_text_title}</h2>}
            <div className="leading-relaxed text-lg whitespace-pre-line" style={{ color: theme.textMuted }}>{currentPage.custom_text}</div>
          </div>
        </section>
      )}

      {has("gallery") && c.gallery?.length > 0 && (
        <section className="py-16 md:py-20 px-6" data-testid="site-gallery">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-center" style={{ color: accent }}>Galerie</p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
              {c.gallery.map((src, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden" style={{ background: theme.bgAlt }}>
                  <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {has("hours") && c.hours && Object.values(c.hours).some((v) => v) && (
        <section className="py-16 md:py-20 px-6" style={{ background: theme.bgAlt }} data-testid="site-hours">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2"><Clock className="w-4 h-4" style={{ color: accent }} /><p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>Horaires d'ouverture</p></div>
            <div className="mt-6 rounded-2xl border divide-y" style={{ background: theme.card, borderColor: theme.cardBorder, borderTopColor: theme.cardBorder }}>
              {["mon","tue","wed","thu","fri","sat","sun"].map((k) => (
                c.hours[k] ? <div key={k} className="px-6 py-3 flex justify-between items-center border-b last:border-0" style={{ borderColor: theme.cardBorder }}><span className="font-semibold" style={{ color: theme.text }}>{DAY_LABELS[k]}</span><span className="text-sm font-mono" style={{ color: theme.textMuted }}>{c.hours[k]}</span></div> : null
              ))}
            </div>
          </div>
        </section>
      )}

      {has("menu") && c.menu?.length > 0 && (
        <section className="py-16 md:py-20 px-6" data-testid="site-menu">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2"><UtensilsCrossed className="w-4 h-4" style={{ color: accent }} /><p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>Menu &amp; tarifs</p></div>
            <div className="mt-6 space-y-3">
              {c.menu.map((item, i) => (
                <div key={i} className="flex items-start justify-between gap-4 pb-3 border-b border-dashed" style={{ borderColor: theme.borderDashed }}>
                  <div className="flex-1 min-w-0"><p className="font-semibold" style={{ color: theme.text }}>{item.name}</p>{item.desc && <p className="text-sm mt-0.5" style={{ color: theme.textMuted }}>{item.desc}</p>}</div>
                  {item.price && <span className="font-mono font-bold shrink-0" style={{ color: accent }}>{item.price}</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {has("location") && typeof co.lat === "number" && typeof co.lng === "number" && (
        <section className="py-16 md:py-20 px-6" style={{ background: theme.bgAlt }}>
          <div className="max-w-4xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-center" style={{ color: accent }}>Où nous trouver</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tight text-center mb-8" style={{ color: theme.text }}>{co.city}</h2>
            <LocationMap lat={co.lat} lng={co.lng} name={co.name} address={co.address} city={co.city} country={co.country} accent={accent} lang="fr" />
          </div>
        </section>
      )}

      {has("contact") && (
        <section id="contact" className="py-16 md:py-24 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: accent }}>Contact</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tight" style={{ color: theme.text }}>{c.contact_intro || "Passez nous voir"}</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-4 text-left">
              {co.address && <div className="rounded-xl border p-4" style={{ background: theme.card, borderColor: theme.cardBorder }}><MapPin className="w-4 h-4" style={{ color: theme.textMuted }} /><p className="mt-2 text-sm font-semibold" style={{ color: theme.text }}>Adresse</p><p className="text-sm" style={{ color: theme.textMuted }}>{co.address}, {co.city}, {co.country}</p></div>}
              {co.contact_email && <div className="rounded-xl border p-4" style={{ background: theme.card, borderColor: theme.cardBorder }}><Mail className="w-4 h-4" style={{ color: theme.textMuted }} /><p className="mt-2 text-sm font-semibold" style={{ color: theme.text }}>Email</p><a href={`mailto:${co.contact_email}`} onClick={() => track("contact_click")} className="text-sm underline" style={{ color: accent }}>{co.contact_email}</a></div>}
              {co.phone && <div className="rounded-xl border p-4" style={{ background: theme.card, borderColor: theme.cardBorder }}><Phone className="w-4 h-4" style={{ color: theme.textMuted }} /><p className="mt-2 text-sm font-semibold" style={{ color: theme.text }}>Téléphone</p><a href={`tel:${co.phone}`} onClick={() => track("phone_click")} className="text-sm underline" style={{ color: accent }}>{co.phone}</a></div>}
            </div>
          </div>
        </section>
      )}

      <footer className="py-10 px-6 border-t" style={{ background: theme.bg, borderColor: theme.cardBorder }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            {c.logo_url && <img src={c.logo_url} alt="" className="h-8 w-8 rounded-full object-cover border" style={{ borderColor: theme.cardBorder }} />}
            <p className="text-sm" style={{ color: theme.textMuted }}>{c.footer_tagline || co.name}</p>
          </div>
          <a href="/" className="text-xs hover:text-[#F97316] flex items-center gap-1.5" style={{ color: theme.textMuted }}><Globe className="w-3 h-3" /> Site créé avec Proxijob</a>
        </div>
      </footer>
    </div>
  );
}
